import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import PasswordField from "../../Components/PasswordField";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import { useApi } from "../../contexts/ApiProvider";
import { useTranslation } from "react-i18next";

type TRequestTokenPassword = {
  token: string;
  password: string;
};

const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const api = useApi();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text: t("Resetting password cancelled"),
      severity: "info",
    });
    navigate("/");
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      passwordConfirmation: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required(t("Password is required")),
      passwordConfirmation: Yup.string().oneOf(
        [Yup.ref("password"), undefined],
        t("Passwords must match")
      ),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const valuesWithToken = {
        token: token,
        password: window.btoa(values.password),
      };
      const response = await api.put<TRequestTokenPassword, null>(
        "/tokens/reset",
        valuesWithToken
      );
      if (response.ok && response.body) {
        showMessage({
          text: t("Password resetted successfully"),
          severity: "success",
        });
      } else {
        showMessage({
          text: "Resetting password failed",
          severity: "error",
        });
      }
      navigate("/");
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <PasswordField
        fullWidth
        margin="normal"
        id="password"
        label={t("Password")}
        disabled={isSubmitting}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.password}
      ></PasswordField>
      <PasswordField
        fullWidth
        margin="normal"
        id="passwordConfirmation"
        label={t("Confirm Password")}
        disabled={isSubmitting}
        error={
          formik.touched.passwordConfirmation &&
          Boolean(formik.errors.passwordConfirmation)
        }
        helperText={
          formik.touched.passwordConfirmation &&
          formik.errors.passwordConfirmation
        }
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.passwordConfirmation}
      ></PasswordField>
      <Box mt={2} mb={2} display="flex" flexDirection="row">
        <Button type="submit" disabled={isSubmitting}>
          {t("Reset")}
        </Button>
        <Link to="/">
          <Button type="reset" disabled={isSubmitting} onClick={handleCancel}>
            {t("Cancel")}
          </Button>
        </Link>
      </Box>
    </form>
  );
};

export default ResetPasswordForm;
